import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

function CountUp({ end, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let s = 0;
        const step = end / 60;
        const t = setInterval(() => {
          s += step;
          if (s >= end) { setCount(end); clearInterval(t); }
          else setCount(Math.floor(s));
        }, 22);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return <span ref={ref}>{count}{suffix}</span>;
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-q">
        <span>{q}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`}></i>
      </div>
      {open && <div className="faq-a">{a}</div>}
    </div>
  );
}

const marqueeItems = ['BPO Services','Debt Recovery','KPO Solutions','HR Outsourcing','Call Center','Skip Tracing','Financial Process','Global Clients','Kotak Bank','IDFC Bank','Karnataka Network','Since 2012'];

export default function Home() {
  return (
    <div className="home-page">
      {/* HERO */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-photo"></div>
          <div className="hero-overlay"></div>
          <div className="hero-lines"></div>
        </div>
        <div className="hero-inner">
          <div className="hero-kicker">
            <span className="hero-kicker-line"></span>
            <span className="hero-kicker-text">Est. 2012 · Bangalore, India · BPO · KPO · Debt Management</span>
          </div>
          <h1 className="hero-title">
            Scale Your Business with<br />
            <span className="italic">Expert</span> Outsourcing<br />
            <span className="em">Solutions</span>
          </h1>
          <p className="hero-sub">
            Helping enterprises grow with tailored BPO, KPO &amp; debt management services. Performance-driven, ethical, and globally scalable from Bangalore to the world.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="btn-gold"><i className="fas fa-rocket"></i> Get Started</Link>
            <Link to="/services" className="btn-outline"><i className="fas fa-layer-group"></i> Our Services</Link>
          </div>
          <div className="hero-strip">
            {[['60','%','Cost Reduction'],['500','+','Clients Served'],['12','+','Years Active']].map(([val,suf,lbl]) => (
              <div className="strip-item" key={lbl}>
                <div className="strip-val"><span className="strip-gold">{val}</span>{suf}</div>
                <div className="strip-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-scroll">
          <span className="scroll-label">Scroll</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {[...marqueeItems,...marqueeItems].map((item,i) => (
            <div className="m-item" key={i}><span className="m-dot"></span>{item}</div>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {[
              {icon:'fa-percent',val:60,suffix:'%',label:'Cost Reduction',desc:'vs in-house operations'},
              {icon:'fa-users',val:500,suffix:'+',label:'Clients Served',desc:'Banks, NBFCs & global firms'},
              {icon:'fa-calendar-check',val:12,suffix:'+',label:'Years Experience',desc:'Founded 2012, Bangalore'},
              {icon:'fa-chart-line',val:95,suffix:'%',label:'Recovery Rate',desc:'Proven collection success'},
            ].map((s,i) => (
              <div key={i} className="stat-card">
                <div className="stat-icon"><i className={`fas ${s.icon}`}></i></div>
                <div className="stat-num"><CountUp end={s.val} suffix={s.suffix} /></div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO STRIP */}
      <div className="photo-strip">
        {[
          {url:'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=800&q=80',label:'Business Process Excellence'},
          {url:'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80',label:'Expert Knowledge Teams'},
          {url:'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80',label:'Global Operations'},
        ].map((p,i) => (
          <div className="photo-strip-item" key={i}>
            <img src={p.url} alt={p.label} loading="lazy" />
            <div className="psi-overlay"></div>
            <div className="psi-label">{p.label}</div>
          </div>
        ))}
      </div>

      {/* SERVICES OVERVIEW */}
      <section className="services-overview">
        <div className="container">
          <div className="section-intro">
            <div className="eyebrow">What We Do</div>
            <h2 className="section-h">Comprehensive Outsourcing <span className="gold">Solutions</span></h2>
            <p className="section-body">From debt recovery to global BPO — we deliver measurable results across every engagement we undertake.</p>
          </div>
          <div className="srv-overview-grid">
            {[
              {tag:'BPO',icon:'fa-headset',color:'var(--emerald)',title:'Business Process Outsourcing',desc:'End-to-end BPO services including inbound/outbound call center, customer support, sales, lead generation and back-office operations.',pills:['Call Center','Customer Support','Sales & Leads']},
              {tag:'KPO',icon:'fa-brain',color:'var(--gold)',title:'Knowledge Process Outsourcing',desc:'High-value, insight-driven services covering data analytics, financial processing, customer insights and compliance reporting.',pills:['Analytics','Financial Services','Research']},
              {tag:'HRO',icon:'fa-users-cog',color:'var(--navy)',title:'Human Resource Outsourcing',desc:'Complete HR solutions including recruitment, payroll management, training & development, and staffing.',pills:['Recruitment','Payroll','Staffing']},
              {tag:'DRC',icon:'fa-file-invoice-dollar',color:'var(--emerald)',title:'Debt Recovery & Collections',desc:'Professional field collections, skip tracing, negotiation & multi-product portfolio recovery across Karnataka.',pills:['Field Recovery','Skip Tracing','Negotiation']},
            ].map((s,i) => (
              <div key={i} className="srv-card">
                <div className="srv-tag-badge">{s.tag}</div>
                <div className="srv-icon" style={{color:s.color}}><i className={`fas ${s.icon}`}></i></div>
                <h3 className="srv-title">{s.title}</h3>
                <p className="srv-desc">{s.desc}</p>
                <div className="srv-pills">{s.pills.map(p => <span key={p} className="pill">{p}</span>)}</div>
                <Link to="/services" className="srv-link">Learn More <i className="fas fa-arrow-right"></i></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="why-section">
        <div className="container">
          <div className="why-grid">
            <div className="why-visual">
              <div className="why-frame">
                <img src="https://images.unsplash.com/photo-1560472355-536de3962603?w=800&q=80" alt="CSR Team" className="why-frame-img" />
                <div className="why-frame-overlay"></div>
                <div className="why-badge">12+<small>Years</small></div>
              </div>
            </div>
            <div>
              <div className="eyebrow">Why Choose Us</div>
              <h2 className="section-h">What Sets CSR <span className="gold">Apart</span></h2>
              <div className="why-features">
                {[
                  ['01','Proven Track Record','Over a decade of measurable results for 500+ clients including Kotak Bank, IDFC Bank, and global NBFCs.'],
                  ['02','Ethical Operations','Full regulatory compliance with TRAI, RBI & GDPR standards — protecting your brand reputation.'],
                  ['03','Karnataka-Wide Network','Deep field operations coverage across all Karnataka districts for unmatched reach.'],
                  ['04','Technology-Driven','AI-powered analytics, skip tracing tools, and modern CRM platforms for faster resolutions.'],
                  ['05','Scalable Infrastructure','Flexible team sizes and service models that grow with your business needs.'],
                ].map(([num,title,desc]) => (
                  <div key={num} className="why-feat">
                    <div className="feat-num">{num}</div>
                    <div className="feat-body">
                      <div className="feat-title">{title}</div>
                      <div className="feat-desc">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="clients-section">
        <div className="container">
          <p className="clients-label">Trusted by Leading Banks, NBFCs &amp; Global Enterprises</p>
          <div className="clients-grid">
            {['Kotak Mahindra Bank','IDFC First Bank','Yes Bank','NBFCs India','Telecom Partners','US Clients','UK Businesses','Fintech Startups','Global Enterprises'].map(c => (
              <div key={c} className="client-pill">{c}</div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <div className="faq-grid">
            <div>
              <div className="eyebrow">FAQ</div>
              <h2 className="section-h">Frequently Asked <span className="gold">Questions</span></h2>
              <p className="section-body" style={{marginTop:'16px'}}>Got questions? We have answers. Reach out if you need more details.</p>
              <div style={{marginTop:'32px'}}>
                <Link to="/contact" className="btn-navy"><i className="fas fa-envelope"></i> Contact Us</Link>
              </div>
            </div>
            <div className="faq-list">
              {[
                ['What industries do you serve?','We serve banks, NBFCs, telecom companies, fintech startups, SMEs, and US/UK businesses requiring outsourcing support.'],
                ['How quickly can you onboard a new client?','Standard onboarding is completed within 5–7 business days, with dedicated account management from day one.'],
                ['Do you handle international clients?','Yes, we actively serve US and UK businesses with English-speaking teams and global-standard processes.'],
                ['What is your debt recovery rate?','Our average collection success rate is 95%+ across personal loans, credit cards, and business loan portfolios.'],
                ['Is data security guaranteed?','Absolutely. We operate under strict GDPR, RBI, and TRAI-compliant data security protocols with end-to-end encryption.'],
              ].map(([q,a]) => <FaqItem key={q} q={q} a={a} />)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-inner">
            <div className="eyebrow" style={{justifyContent:'center'}}>Partner With Us</div>
            <h2 className="section-h" style={{color:'#fff'}}>Ready to <span className="gold">Transform</span> Your Operations?</h2>
            <p className="cta-sub">Join hundreds of businesses that trust CSR's Financial Solutions for scalable, cost-effective outsourcing.</p>
            <div className="cta-btns">
              <Link to="/contact" className="btn-gold"><i className="fas fa-paper-plane"></i> Get in Touch</Link>
              <Link to="/services" className="btn-outline" style={{borderColor:'rgba(255,255,255,0.3)',color:'rgba(255,255,255,0.8)'}}>View Services</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
